'use client'

import { useEffect, useState } from 'react'
import { useAuth } from '@/store/hooks'
import { useNavigate } from 'react-router-dom'
import { toast } from 'react-toastify'
import {
    MdArrowBack,
    MdSearch,
    MdClose,
    MdHistory,
    MdRefresh,
    MdFilterList,
    MdVisibility,
    MdPerson,
} from 'react-icons/md'
import {
    getRndsTransactions,
    retryRndsTransaction,
    RndsTransaction,
    RndsStatus,
    RndsEventType,
    RNDS_STATUS_LABELS,
    RNDS_STATUS_COLORS,
    RNDS_EVENT_TYPE_LABELS,
} from '@/lib/api/health-integrations/rnds-api'

export default function RndsTransactionsPage() {
    const { user } = useAuth()
    const navigate = useNavigate()

    const [transactions, setTransactions] = useState<RndsTransaction[]>([])
    const [filteredTransactions, setFilteredTransactions] = useState<
        RndsTransaction[]
    >([])
    const [loading, setLoading] = useState(true)
    const [searchTerm, setSearchTerm] = useState('')
    const [statusFilter, setStatusFilter] = useState<RndsStatus | ''>('')
    const [typeFilter, setTypeFilter] = useState<RndsEventType | ''>('')
    const [selectedTransaction, setSelectedTransaction] =
        useState<RndsTransaction | null>(null)
    const [retrying, setRetrying] = useState<number | null>(null)

    useEffect(() => {
        if (user?.companyId) {
            loadTransactions()
        }
    }, [user?.companyId, statusFilter, typeFilter])

    useEffect(() => {
        if (searchTerm.trim()) {
            const filtered = transactions.filter(
                transaction =>
                    transaction.client?.name
                        .toLowerCase()
                        .includes(searchTerm.toLowerCase()) ||
                    transaction.professional?.name
                        .toLowerCase()
                        .includes(searchTerm.toLowerCase()) ||
                    transaction.fhirResourceId
                        ?.toLowerCase()
                        .includes(searchTerm.toLowerCase())
            )
            setFilteredTransactions(filtered)
        } else {
            setFilteredTransactions(transactions)
        }
    }, [searchTerm, transactions])

    const loadTransactions = async () => {
        setLoading(true)
        try {
            const filters: any = {}
            if (statusFilter) filters.status = statusFilter
            if (typeFilter) filters.eventType = typeFilter

            const response = await getRndsTransactions(filters)
            if (response.success) {
                setTransactions(response.result)
                setFilteredTransactions(response.result)
            } else {
                toast.error(response.error || 'Erro ao carregar transações')
            }
        } catch (error) {
            toast.error('Erro ao carregar transações')
        } finally {
            setLoading(false)
        }
    }

    const handleRetry = async (id: number) => {
        setRetrying(id)
        try {
            const response = await retryRndsTransaction(id)
            if (response.success) {
                toast.success('Transação reenviada')
                loadTransactions()
            } else {
                toast.error(response.error || 'Erro ao reenviar transação')
            }
        } catch (error) {
            toast.error('Erro ao reenviar transação')
        } finally {
            setRetrying(null)
        }
    }

    if (loading) {
        return (
            <div className="flex flex-col items-center justify-center min-h-[400px] gap-4">
                <div className="loading loading-spinner loading-lg text-secondary"></div>
                <p className="text-base-content/60 text-sm">
                    Carregando transações...
                </p>
            </div>
        )
    }

    return (
        <div className="flex flex-col gap-6">
            <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                    <button
                        onClick={() => navigate('/health-integrations/rnds')}
                        className="btn btn-ghost btn-sm"
                    >
                        <MdArrowBack className="text-xl" />
                    </button>
                    <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-teal-500 to-teal-600 flex items-center justify-center shadow-lg">
                            <MdHistory className="text-2xl text-white" />
                        </div>
                        <div>
                            <h1 className="text-2xl font-bold text-base-content">
                                Transações RNDS
                            </h1>
                            <p className="text-sm text-base-content/60">
                                {transactions.length}{' '}
                                {transactions.length === 1
                                    ? 'transação'
                                    : 'transações'}
                            </p>
                        </div>
                    </div>
                </div>
                <button
                    onClick={loadTransactions}
                    className="btn btn-ghost btn-sm gap-2"
                >
                    <MdRefresh className="text-xl" />
                    Atualizar
                </button>
            </div>

            <div className="bg-base-100 rounded-xl border border-base-300/50 shadow-sm p-4">
                <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2">
                    <div className="flex-1 relative">
                        <MdSearch className="absolute z-10 left-3 top-1/2 -translate-y-1/2 text-base-content/70 text-xl pointer-events-none z-10" />
                        <input
                            type="text"
                            placeholder="Buscar por paciente, profissional ou ID..."
                            className="input input-bordered w-full pl-10"
                            value={searchTerm}
                            onChange={e => setSearchTerm(e.target.value)}
                        />
                    </div>

                    <div className="flex items-center gap-2">
                        <MdFilterList className="text-xl text-base-content/60" />
                        <select
                            className="select select-bordered select-sm"
                            value={statusFilter}
                            onChange={e =>
                                setStatusFilter(
                                    e.target.value as RndsStatus | ''
                                )
                            }
                        >
                            <option value="">Status</option>
                            {Object.entries(RNDS_STATUS_LABELS).map(
                                ([value, label]) => (
                                    <option key={value} value={value}>
                                        {label}
                                    </option>
                                )
                            )}
                        </select>
                        <select
                            className="select select-bordered select-sm"
                            value={typeFilter}
                            onChange={e =>
                                setTypeFilter(
                                    e.target.value as RndsEventType | ''
                                )
                            }
                        >
                            <option value="">Tipo</option>
                            {Object.entries(RNDS_EVENT_TYPE_LABELS).map(
                                ([value, label]) => (
                                    <option key={value} value={value}>
                                        {label}
                                    </option>
                                )
                            )}
                        </select>
                    </div>

                    {searchTerm && (
                        <button
                            type="button"
                            onClick={() => setSearchTerm('')}
                            className="btn btn-ghost btn-sm"
                        >
                            <MdClose className="text-lg" />
                        </button>
                    )}
                </div>
            </div>

            {filteredTransactions.length === 0 ? (
                <div className="flex flex-col items-center justify-center min-h-[400px] gap-4 bg-base-100 rounded-xl border border-base-300/50 p-8">
                    <div className="w-20 h-20 rounded-full bg-base-200 flex items-center justify-center">
                        <MdHistory className="text-4xl text-base-content/30" />
                    </div>
                    <div className="text-center">
                        <h3 className="text-lg font-semibold text-base-content mb-1">
                            Nenhuma transação encontrada
                        </h3>
                        <p className="text-sm text-base-content/60">
                            {searchTerm || statusFilter || typeFilter
                                ? 'Tente ajustar seus filtros'
                                : 'Transações RNDS aparecerão aqui'}
                        </p>
                    </div>
                </div>
            ) : (
                <div className="bg-base-100 rounded-xl border border-base-300/50 overflow-hidden">
                    <div className="overflow-x-auto">
                        <table className="table">
                            <thead>
                                <tr>
                                    <th>Tipo</th>
                                    <th>Paciente</th>
                                    <th>Profissional</th>
                                    <th>Status</th>
                                    <th>Data</th>
                                    <th>Ações</th>
                                </tr>
                            </thead>
                            <tbody>
                                {filteredTransactions.map(transaction => (
                                    <tr key={transaction.id} className="hover">
                                        <td>
                                            <span className="badge badge-outline">
                                                {
                                                    RNDS_EVENT_TYPE_LABELS[
                                                        transaction.eventType
                                                    ]
                                                }
                                            </span>
                                        </td>
                                        <td>
                                            {transaction.client ? (
                                                <div className="flex items-center gap-2">
                                                    <MdPerson className="text-lg text-base-content/60" />
                                                    <span>
                                                        {
                                                            transaction.client
                                                                .name
                                                        }
                                                    </span>
                                                </div>
                                            ) : (
                                                '-'
                                            )}
                                        </td>
                                        <td>
                                            {transaction.professional?.name ||
                                                '-'}
                                        </td>
                                        <td>
                                            <span
                                                className={`badge ${RNDS_STATUS_COLORS[transaction.status]}`}
                                            >
                                                {
                                                    RNDS_STATUS_LABELS[
                                                        transaction.status
                                                    ]
                                                }
                                            </span>
                                        </td>
                                        <td>
                                            {new Date(
                                                transaction.createdAt
                                            ).toLocaleString('pt-BR')}
                                        </td>
                                        <td>
                                            <div className="flex items-center gap-1">
                                                <button
                                                    onClick={() =>
                                                        setSelectedTransaction(
                                                            transaction
                                                        )
                                                    }
                                                    className="btn btn-ghost btn-sm"
                                                    title="Ver detalhes"
                                                >
                                                    <MdVisibility className="text-lg" />
                                                </button>
                                                {(transaction.status ===
                                                    'ERROR' ||
                                                    transaction.status ===
                                                        'REJECTED') && (
                                                    <button
                                                        onClick={() =>
                                                            handleRetry(
                                                                transaction.id
                                                            )
                                                        }
                                                        className="btn btn-ghost btn-sm"
                                                        title="Reenviar"
                                                        disabled={
                                                            retrying ===
                                                            transaction.id
                                                        }
                                                    >
                                                        {retrying ===
                                                        transaction.id ? (
                                                            <span className="loading loading-spinner loading-sm"></span>
                                                        ) : (
                                                            <MdRefresh className="text-lg" />
                                                        )}
                                                    </button>
                                                )}
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}

            {/* Transaction Detail Modal */}
            {selectedTransaction && (
                <dialog open className="modal modal-open">
                    <div className="modal-box max-w-3xl">
                        <h3 className="font-bold text-lg mb-4">
                            Detalhes da Transação
                        </h3>

                        <div className="space-y-4">
                            <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                                <div>
                                    <label className="text-sm text-base-content/60">
                                        Tipo de Evento
                                    </label>
                                    <p className="font-medium">
                                        {
                                            RNDS_EVENT_TYPE_LABELS[
                                                selectedTransaction.eventType
                                            ]
                                        }
                                    </p>
                                </div>
                                <div>
                                    <label className="text-sm text-base-content/60">
                                        Status
                                    </label>
                                    <p>
                                        <span
                                            className={`badge ${RNDS_STATUS_COLORS[selectedTransaction.status]}`}
                                        >
                                            {
                                                RNDS_STATUS_LABELS[
                                                    selectedTransaction.status
                                                ]
                                            }
                                        </span>
                                    </p>
                                </div>
                                <div>
                                    <label className="text-sm text-base-content/60">
                                        ID FHIR
                                    </label>
                                    <p className="font-medium font-mono text-xs">
                                        {selectedTransaction.fhirResourceId ||
                                            '-'}
                                    </p>
                                </div>
                            </div>

                            <div className="divider"></div>

                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label className="text-sm text-base-content/60">
                                        Paciente
                                    </label>
                                    <p className="font-medium">
                                        {selectedTransaction.client?.name ||
                                            '-'}
                                    </p>
                                </div>
                                <div>
                                    <label className="text-sm text-base-content/60">
                                        Profissional
                                    </label>
                                    <p className="font-medium">
                                        {selectedTransaction.professional
                                            ?.name || '-'}
                                    </p>
                                </div>
                            </div>

                            {selectedTransaction.errorMessage && (
                                <div className="alert alert-error">
                                    <span>
                                        {selectedTransaction.errorMessage}
                                    </span>
                                </div>
                            )}

                            {selectedTransaction.fhirResource && (
                                <div>
                                    <label className="text-sm text-base-content/60">
                                        Recurso FHIR
                                    </label>
                                    <pre className="bg-base-200 p-4 rounded-lg text-xs overflow-x-auto max-h-60">
                                        {JSON.stringify(
                                            selectedTransaction.fhirResource,
                                            null,
                                            2
                                        )}
                                    </pre>
                                </div>
                            )}
                        </div>

                        <div className="modal-action">
                            <button
                                className="btn"
                                onClick={() => setSelectedTransaction(null)}
                            >
                                Fechar
                            </button>
                        </div>
                    </div>
                    <form method="dialog" className="modal-backdrop">
                        <button onClick={() => setSelectedTransaction(null)}>
                            close
                        </button>
                    </form>
                </dialog>
            )}
        </div>
    )
}
